import RestaurantPlaceholder from '@/components/display/restaurant-placeholder';
import { Rating } from '@/components/ui/rating-distribution';
import { Text } from '@/components/ui/text';
import { Restaurant } from '@/types/display';
import { AppImage } from '@/components/ui/app-image';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import { useState } from 'react';
import { FlatList, Pressable, View } from 'react-native';

interface RestaurantListProps {
  items: Restaurant[];
  onPress?: (restaurant: Restaurant) => void;
}

const RestaurantListItem = ({ restaurant }: { restaurant: Restaurant }) => {
  const [imageError, setImageError] = useState(false);
  const hasImage = !!restaurant.image && !imageError;

  const handlePress = () => {
    router.push({
      pathname: '/(root)/business/[id]',
      params: { id: restaurant.id },
    });
  };

  return (
    <Pressable
      onPress={handlePress}
      className="mb-4 bg-surface rounded-xl overflow-hidden"
      style={{
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}
    >
      <View className="flex-row">
        {/* Thumbnail */}
        {hasImage ? (
          <AppImage
            r2Key={restaurant.image}
            preset="thumbnail"
            style={{ width: 96, height: 96 }}
            onError={() => setImageError(true)}
            accessibilityLabel={`Photo of ${restaurant.name}`}
          />
        ) : (
          <RestaurantPlaceholder name={restaurant.name} width={96} height={96} />
        )}

        {/* Content */}
        <View className="flex-1 p-3 justify-between">
          <View>
            <Text className="font-bold text-base mb-1 text-foreground">
              {restaurant.name}
            </Text>
            <View className="flex-row items-center mb-1">
              <Text className="text-sm mr-2 text-muted-foreground">
                {restaurant.cuisine}
              </Text>
              {restaurant.distance && (
                <Text className="text-sm text-muted-foreground">
                  • {restaurant.distance}
                </Text>
              )}
            </View>
          </View>

          {/* Rating */}
          <View className="flex-row items-end">
            {restaurant.ratingDistribution ? (
              <>
                <Rating
                  distribution={restaurant.ratingDistribution}
                  size="sm"
                />
                <Text className="text-xs ml-2 text-muted-foreground">
                  {restaurant.reviews > 0 ? `${restaurant.reviews} ratings` : 'No ratings yet'}
                </Text>
              </>
            ) : (
              <Text className="text-xs text-muted-foreground">
                No ratings yet
              </Text>
            )}
          </View>
        </View>
      </View>
    </Pressable>
  );
};

export const RestaurantList: React.FC<RestaurantListProps> = ({ items, onPress }) => {
  if (items.length === 0) {
    return (
      <View className="flex-1 items-center justify-center py-16 px-4">
        <Ionicons name="restaurant-outline" size={64} className="text-muted-foreground" />
        <Text className="text-lg font-semibold mt-4 mb-2 text-foreground">
          No restaurants found
        </Text>
        <Text className="text-sm text-center text-muted-foreground">
          Try adjusting your filters or search criteria
        </Text>
      </View>
    );
  }

  return (
    <FlatList
      data={items}
      renderItem={({ item }) => <RestaurantListItem restaurant={item} />}
      keyExtractor={(item) => item.id}
      contentContainerStyle={{ padding: 16 }}
      showsVerticalScrollIndicator={false}
    />
  );
};
